import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { CheckCircle2, Heart, Loader2, Home } from 'lucide-react';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DonateSuccess = () => {
  const [donation, setDonation] = useState(null);
  const [status, setStatus] = useState('checking');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const donationId = params.get('donationId');
    if (!donationId) {
      setStatus('unknown');
      return;
    }

    let attempts = 0;
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      try {
        const res = await axios.get(`${API}/donations/${donationId}`);
        setDonation(res.data);
        if (res.data.status === 'paid') {
          setStatus('paid');
          return;
        }
        if (res.data.status === 'failed') {
          setStatus('failed');
          return;
        }
      } catch {
        // ignore
      }
      attempts += 1;
      if (attempts < 12) {
        setTimeout(poll, 2500);
      } else {
        setStatus('pending');
      }
    };
    poll();
    return () => { cancelled = true; };
  }, []);

  const amount = donation ? (donation.amount_cents / 100).toFixed(2) : '';
  const meals = donation ? Math.floor((donation.amount_cents / 100) / 10) : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FFF7F1] via-[#FFEFE2] to-[#FFE4CF] flex items-center justify-center px-6 py-24">
      <div className="max-w-xl w-full bg-white rounded-3xl shadow-2xl p-10 text-center border border-[#FFE4CF]">
        {status === 'paid' && (
          <>
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#F55B26] to-[#FF8A3D] flex items-center justify-center mx-auto mb-6 shadow-lg">
              <CheckCircle2 className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-3xl font-extrabold text-[#0B3B44] mb-3">Thank you, {donation?.name?.split(' ')[0]}!</h1>
            <p className="text-slate-700 mb-6 leading-relaxed">
              Your donation of <strong className="text-[#F55B26]">R{amount}</strong> has been received.
              You&apos;ve just funded approximately <strong>{meals} meals</strong> for those who need it most.
            </p>
            <div className="bg-[#FFF7F1] rounded-2xl p-5 mb-6 text-left">
              <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-1">Receipt sent to</div>
              <div className="font-bold text-[#0B3B44]">{donation?.email}</div>
            </div>
          </>
        )}

        {status === 'checking' && (
          <>
            <div className="w-20 h-20 rounded-full bg-[#FFEFE2] flex items-center justify-center mx-auto mb-6">
              <Loader2 className="w-10 h-10 text-[#F55B26] animate-spin" />
            </div>
            <h1 className="text-2xl font-extrabold text-[#0B3B44] mb-2">Confirming your donation...</h1>
            <p className="text-slate-600 mb-6">This usually takes just a few seconds.</p>
          </>
        )}

        {status === 'pending' && (
          <>
            <div className="w-20 h-20 rounded-full bg-[#FFEFE2] flex items-center justify-center mx-auto mb-6">
              <Heart className="w-10 h-10 text-[#F55B26]" />
            </div>
            <h1 className="text-2xl font-extrabold text-[#0B3B44] mb-2">Payment processing</h1>
            <p className="text-slate-600 mb-6">
              We&apos;re still confirming your payment. If you completed the transaction, you&apos;ll receive an email shortly.
            </p>
          </>
        )}

        {status === 'failed' && (
          <>
            <div className="w-20 h-20 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-6">
              <Heart className="w-10 h-10 text-red-500" />
            </div>
            <h1 className="text-2xl font-extrabold text-[#0B3B44] mb-2">Payment did not complete</h1>
            <p className="text-slate-600 mb-6">Please try again or reach out to us if the issue continues.</p>
          </>
        )}

        {status === 'unknown' && (
          <>
            <h1 className="text-2xl font-extrabold text-[#0B3B44] mb-2">Nothing here</h1>
            <p className="text-slate-600 mb-6">This donation link seems invalid.</p>
          </>
        )}

        <Button
          onClick={() => (window.location.href = '/')}
          className="bg-[#F55B26] hover:bg-[#E14D1F] text-white font-semibold px-6 rounded-full"
        >
          <Home className="w-4 h-4 mr-2" />
          Return home
        </Button>
      </div>
    </div>
  );
};

export default DonateSuccess;
